import {
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  Service,
} from '@angular/core';
import {
  OfflineStorageService,
  PendingAction,
} from './offline-storage.service';
import { NetworkStatusService } from './network-status.service';
import {
  ChecklistHttpService,
  DeleteFileRollbackContext,
} from '../checklist-http.service';
import { firstValueFrom, Observable } from 'rxjs';
import { ChecklistStoreService } from '../checklist-store.service';
import { ChecklistLoaderService } from '../checklist-loader.service';
import { ChecklistFileRefsService } from '../checklist-file-refs.service';
import { environment } from '../../../environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { AppToastService } from '../app-toast.service';
import { ItemWriteConflictService } from '../item-write-conflict.service';
import { mergeTexts, payloadValue } from '../../utils/item-write-conflict.util';
import { parseCustomInputContent } from '../../modules/components/fields/custom-inputs/custom-input.parser';

type ConditionalActionType =
  | 'updateChecklistItemRemarks'
  | 'updateChecklistItemHeader'
  | 'updateChecklistItemCustomInput';

// Must match each method's parameter order, leading ids then value then previous.
const ID_PARAMS_BEFORE_VALUE: Record<ConditionalActionType, number> = {
  updateChecklistItemRemarks: 3,
  updateChecklistItemHeader: 3,
  updateChecklistItemCustomInput: 4,
};

const DIAGNOSTIC_ID_PARAMS: Partial<
  Record<keyof ChecklistHttpService, string[]>
> = {
  uploadFile: ['checklistId', 'itemId', 'customInputId', 'fileId'],
  deleteFile: ['checklistId', 'itemId', 'fileId'],
};

const DEFAULT_DIAGNOSTIC_ID_PARAMS = ['checklistId', 'sectionId', 'itemId'];

const RETRY_BACKOFF_MS = 10_000;

type QueueStep = 'next' | 'stop';

interface QueuedConditionalWrite {
  actionType: ConditionalActionType;
  ids: string[];
  local: unknown;
  previous: unknown;
}

interface ReplayedFieldWrite {
  local: unknown;
  landed: unknown;
  discarded: boolean;
}

@Service()
export class SyncService {
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly checklistHttpService = inject(ChecklistHttpService);
  private readonly store = inject(ChecklistStoreService);
  private readonly checklistLoader = inject(ChecklistLoaderService);
  private readonly fileRefs = inject(ChecklistFileRefsService);
  private readonly appToastService = inject(AppToastService);
  private readonly itemWriteConflictService = inject(ItemWriteConflictService);
  private readonly shouldSync = computed(
    () =>
      this.networkStatusService.online() &&
      this.hasActionsToSync() &&
      !this.isSyncing()
  );
  hasActionsToSync = computed(
    () => this.offlineStorage.pendingActions().length > 0
  );

  private readonly isSyncing = signal(false);
  private readonly rejectedUploadFileIds = new Set<string>();
  private readonly retryTick = signal(0);
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryQueueLength = 0;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancelRetryBackoff());

    effect(() => {
      if (environment.testMode) {
        return;
      }
      this.retryTick();
      const online = this.networkStatusService.online();
      const queueLength = this.offlineStorage.pendingActions().length;
      if (this.retryTimer !== null) {
        if (online && queueLength <= this.retryQueueLength) {
          return;
        }
        this.cancelRetryBackoff();
      }
      void this.syncData();
    });

    if (navigator.serviceWorker?.addEventListener) {
      navigator.serviceWorker.addEventListener('message', async event => {
        if (event.data?.type === 'SYNC_OFFLINE_ACTIONS') {
          await this.syncData();
        }
      });
    }
  }

  async syncData() {
    if (!this.shouldSync()) return;
    this.isSyncing.set(true);
    this.rejectedUploadFileIds.clear();

    let stopped = false;
    try {
      const syncedChecklists = new Set<string>();
      const replayedFieldWrites = new Map<string, ReplayedFieldWrite>();
      for (const action of this.offlineStorage.pendingActions()) {
        const step = await this.processQueuedAction(
          action,
          syncedChecklists,
          replayedFieldWrites
        );
        if (step === 'stop') {
          stopped = true;
          break;
        }
      }

      await this.refreshSyncedChecklists(syncedChecklists);
    } finally {
      this.rejectedUploadFileIds.clear();
      if (stopped) {
        this.startRetryBackoff();
      }
      this.isSyncing.set(false);
    }
  }

  private startRetryBackoff() {
    this.cancelRetryBackoff();
    this.retryQueueLength = this.offlineStorage.pendingActions().length;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.retryTick.update(tick => tick + 1);
    }, RETRY_BACKOFF_MS);
  }

  private cancelRetryBackoff() {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private async processQueuedAction(
    action: PendingAction,
    syncedChecklists: Set<string>,
    replayedFieldWrites: Map<string, ReplayedFieldWrite>
  ): Promise<QueueStep> {
    if (!(await this.offlineStorage.isFirstAction(action))) {
      console.warn(
        'Queued sync action was no longer the queue head; stopping queue processing.'
      );
      await this.offlineStorage.refreshPendingActions();
      return 'stop';
    }

    if (this.isPrototypeWrite(action)) {
      return this.skipPrototypeWrite(action);
    }

    this.offlineStorage.markActionInFlight(action);
    try {
      await this.replayAction(action, replayedFieldWrites);
      await this.markUploadConfirmed(action.actionType, action.params);
      if (!(await this.removeProcessedAction(action))) {
        return 'stop';
      }
      if (typeof action.params[0] === 'string') {
        syncedChecklists.add(action.params[0]);
      }
      return 'next';
    } catch (error) {
      return await this.handleFailedAction(action, error);
    } finally {
      this.offlineStorage.clearActionInFlight(action);
    }
  }

  private isPrototypeWrite(action: PendingAction): boolean {
    return (
      action.actionType !== 'addRepeatableChecklistItem' &&
      this.isQueuedActionTargetingPrototype(action.actionType, action.params)
    );
  }

  private async skipPrototypeWrite(action: PendingAction): Promise<QueueStep> {
    console.warn(
      `Skipping queued ${String(action.actionType)} action targeting a repeatable prototype.`
    );
    return (await this.removeProcessedAction(action)) ? 'next' : 'stop';
  }

  private async replayAction(
    action: PendingAction,
    replayedFieldWrites: Map<string, ReplayedFieldWrite>
  ): Promise<void> {
    const conditionalWrite = this.readConditionalWrite(
      action.actionType,
      action.params
    );
    if (conditionalWrite) {
      await this.replayConditionalWrite(conditionalWrite, replayedFieldWrites);
      return;
    }
    await this.sendToServer(
      action.actionType,
      action.params as Parameters<
        ChecklistHttpService[typeof action.actionType]
      >
    );
  }

  private async handleFailedAction(
    action: PendingAction,
    error: unknown
  ): Promise<QueueStep> {
    const diagnostic = this.buildSyncFailureDiagnostic(
      action.actionType,
      action.params,
      error
    );
    const imageFailureResult = await this.handleNonQueueableImageActionFailure(
      action,
      error
    );
    if (imageFailureResult === 'handled') {
      console.error(
        'Discarding non-queueable image sync action.',
        diagnostic,
        error
      );
      return 'next';
    }
    if (imageFailureResult === 'queue-changed') {
      return 'stop';
    }
    const permanentFailureResult = await this.handlePermanentActionFailure(
      action,
      error
    );
    if (permanentFailureResult === 'handled') {
      console.error(
        'Discarding sync action that targets a resource the server no longer has.',
        diagnostic,
        error
      );
      return 'next';
    }
    if (permanentFailureResult === 'queue-changed') {
      return 'stop';
    }
    console.error(
      'Sync action failed; stopping queue processing and retaining queue head.',
      diagnostic,
      error
    );
    return 'stop';
  }

  private async removeProcessedAction(action: {
    actionType: keyof ChecklistHttpService;
    params: unknown[];
  }): Promise<boolean> {
    const removed =
      await this.offlineStorage.removeFirstActionIfMatches(action);
    if (!removed) {
      console.warn(
        'Processed sync action was no longer the queue head; stopping queue processing.'
      );
      await this.offlineStorage.refreshPendingActions();
    }
    return removed;
  }

  private buildSyncFailureDiagnostic(
    actionType: keyof ChecklistHttpService,
    params: unknown[],
    error: unknown
  ): Record<string, unknown> {
    const diagnostic: Record<string, unknown> = { actionType };
    const idNames =
      DIAGNOSTIC_ID_PARAMS[actionType] ?? DEFAULT_DIAGNOSTIC_ID_PARAMS;
    idNames.forEach((name, index) => {
      if (typeof params[index] === 'string') {
        diagnostic[name] = params[index];
      }
    });
    if (error instanceof HttpErrorResponse) {
      diagnostic['status'] = error.status;
    }
    return diagnostic;
  }

  private isQueuedActionTargetingPrototype(
    actionType: keyof ChecklistHttpService,
    params: unknown[]
  ): boolean {
    if (!actionType.toString().startsWith('updateChecklistItem')) {
      return false;
    }
    const checklistId = params[0];
    const sectionId = params[1];
    const itemId = params[2];
    if (
      typeof checklistId !== 'string' ||
      typeof sectionId !== 'string' ||
      typeof itemId !== 'string'
    ) {
      return false;
    }
    const checklist = this.store.checklists().find(c => c.id === checklistId);
    const section = checklist?.sections.find(s => s.id === sectionId);
    const item = section?.items.find(i => i.id === itemId);
    return item?.isRepeatablePrototype === true;
  }

  private readConditionalWrite(
    actionType: keyof ChecklistHttpService,
    params: unknown[]
  ): QueuedConditionalWrite | null {
    const idCount = ID_PARAMS_BEFORE_VALUE[actionType as ConditionalActionType];
    if (idCount === undefined || params[idCount + 1] === undefined) {
      return null;
    }
    const ids = params.slice(0, idCount);
    if (!ids.every(id => typeof id === 'string')) {
      return null;
    }
    return {
      actionType: actionType as ConditionalActionType,
      ids: ids as string[],
      local: params[idCount],
      previous: params[idCount + 1],
    };
  }

  private async replayConditionalWrite(
    write: QueuedConditionalWrite,
    replayed: Map<string, ReplayedFieldWrite>
  ): Promise<void> {
    const fieldKey = fieldKeyOf(write);
    const earlier = replayed.get(fieldKey);
    const continued =
      earlier !== undefined && sameValue(earlier.local, write.previous)
        ? earlier
        : undefined;
    const outcome =
      write.actionType === 'updateChecklistItemCustomInput'
        ? await this.replayCustomInputWrite(write, continued)
        : await this.replayTextWrite(write, continued);
    replayed.set(fieldKey, outcome);
  }

  private async replayCustomInputWrite(
    write: QueuedConditionalWrite,
    continued: ReplayedFieldWrite | undefined
  ): Promise<ReplayedFieldWrite> {
    const local = payloadValue(write.local);
    const isNumber = local === null || typeof local === 'number';
    const multiline = !isNumber && this.isLongTextInput(write.ids);
    if (continued?.discarded) {
      this.appToastService.showNumberEditDiscarded(String(local ?? ''));
      return { ...continued, local: write.local };
    }
    const result = await this.itemWriteConflictService.writePayload({
      send: (payload, previous) =>
        this.checklistHttpService.updateChecklistItemCustomInput(
          write.ids[0],
          write.ids[1],
          write.ids[2],
          write.ids[3],
          payload,
          previous
        ),
      local:
        continued && !isNumber
          ? {
              value: mergeOntoPreviousValue(
                textOf(continued.local),
                textOf(continued.landed),
                textOf(write.local),
                multiline
              ),
            }
          : write.local,
      previous: continued ? continued.landed : write.previous,
      kind: isNumber ? 'number' : 'text',
      multiline,
    });
    return {
      local: write.local,
      landed: result.landed,
      discarded: isNumber && result.conflicted,
    };
  }

  private isLongTextInput([
    checklistId,
    sectionId,
    itemId,
    customInputId,
  ]: string[]): boolean {
    const customInput = this.store
      .checklists()
      .find(c => c.id === checklistId)
      ?.sections.find(s => s.id === sectionId)
      ?.items.find(i => i.id === itemId)
      ?.customInputs?.find(input => input.id === customInputId);
    if (!customInput) return false;
    const parsed = parseCustomInputContent(
      customInput.customInputTypeId,
      customInput.inputContent
    );
    return parsed.type === 'string_field' && parsed.answerType === 'long';
  }

  private async replayTextWrite(
    write: QueuedConditionalWrite,
    continued: ReplayedFieldWrite | undefined
  ): Promise<ReplayedFieldWrite> {
    const isRemarks = write.actionType === 'updateChecklistItemRemarks';
    const local = typeof write.local === 'string' ? write.local : '';
    const method = isRemarks
      ? this.checklistHttpService.updateChecklistItemRemarks
      : this.checklistHttpService.updateChecklistItemHeader;
    const result = await this.itemWriteConflictService.writeText({
      send: (text, previous) =>
        method.call(
          this.checklistHttpService,
          write.ids[0],
          write.ids[1],
          write.ids[2],
          text,
          previous
        ),
      local: continued
        ? mergeOntoPreviousValue(
            String(continued.local ?? ''),
            String(continued.landed ?? ''),
            local,
            isRemarks
          )
        : local,
      previous: String(continued ? continued.landed : write.previous),
      multiline: isRemarks,
    });
    return { local: write.local, landed: result.landed, discarded: false };
  }

  async sendToServer<T extends keyof ChecklistHttpService>(
    actionType: T,
    params: Parameters<ChecklistHttpService[T]>
  ) {
    if (
      this.checklistHttpService[actionType] &&
      typeof this.checklistHttpService[actionType] === 'function'
    ) {
      const resolvedParams = (await this.resolveActionParams(
        actionType,
        params
      )) as Parameters<ChecklistHttpService[T]>;
      const method = this.checklistHttpService[actionType] as (
        ...args: Parameters<ChecklistHttpService[T]>
      ) => Observable<unknown>;
      await firstValueFrom(
        method.apply(this.checklistHttpService, resolvedParams)
      );
    } else {
      throw new Error(`Unknown action type: ${actionType}`);
    }
  }

  private async resolveActionParams<T extends keyof ChecklistHttpService>(
    actionType: T,
    params: Parameters<ChecklistHttpService[T]>
  ): Promise<unknown[]> {
    if (actionType !== 'uploadFile') {
      return [...params];
    }

    return this.resolveUploadFileParams(params as unknown[]);
  }

  private async resolveUploadFileParams(
    params: unknown[]
  ): Promise<Parameters<ChecklistHttpService['uploadFile']>> {
    const [checklistId, itemId, customInputId, fileId, fileOrMetadata] = params;
    if (
      typeof checklistId !== 'string' ||
      typeof itemId !== 'string' ||
      typeof customInputId !== 'string' ||
      typeof fileId !== 'string'
    ) {
      throw new Error('Invalid uploadFile action parameters');
    }

    if (fileOrMetadata instanceof File) {
      return [checklistId, itemId, customInputId, fileId, fileOrMetadata];
    }

    const blob = await this.offlineStorage.getBlob(fileId);
    if (!blob) {
      throw new Error(`Missing pending upload blob: ${fileId}`);
    }

    const metadata = this.parseUploadActionMetadata(fileOrMetadata);
    const file = new File([blob], metadata.fileName || fileId, {
      type: metadata.contentType || blob.type,
    });
    return [checklistId, itemId, customInputId, fileId, file];
  }

  private parseUploadActionMetadata(value: unknown): {
    fileName: string;
    contentType: string;
  } {
    if (typeof value !== 'object' || value === null) {
      return { fileName: '', contentType: '' };
    }

    const candidate = value as Record<string, unknown>;
    return {
      fileName:
        typeof candidate['fileName'] === 'string' ? candidate['fileName'] : '',
      contentType:
        typeof candidate['contentType'] === 'string'
          ? candidate['contentType']
          : '',
    };
  }

  private async handleNonQueueableImageActionFailure(
    action: {
      actionType: keyof ChecklistHttpService;
      params: unknown[];
    },
    error: unknown
  ): Promise<'handled' | 'unhandled' | 'queue-changed'> {
    if (!this.isNonQueueableHttpFailure(error)) {
      return 'unhandled';
    }

    if (action.actionType === 'uploadFile') {
      return (await this.rollbackRejectedUpload(action))
        ? 'handled'
        : 'queue-changed';
    }

    if (action.actionType === 'deleteFile') {
      return (await this.rollbackRejectedDelete(action))
        ? 'handled'
        : 'queue-changed';
    }

    return 'unhandled';
  }

  private isNonQueueableHttpFailure(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status >= 400 &&
      error.status < 500
    );
  }

  // 404/410 can never succeed on retry and would jam the queue head, so the action is discarded and
  // processing continues, while 401/403/409/5xx still retain it for a later retry.
  private async handlePermanentActionFailure(
    action: {
      actionType: keyof ChecklistHttpService;
      params: unknown[];
    },
    error: unknown
  ): Promise<'handled' | 'unhandled' | 'queue-changed'> {
    if (!this.isPermanentHttpFailure(error)) {
      return 'unhandled';
    }
    return (await this.removeProcessedAction(action))
      ? 'handled'
      : 'queue-changed';
  }

  private isPermanentHttpFailure(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      (error.status === 404 || error.status === 410)
    );
  }

  private async rollbackRejectedUpload(action: {
    actionType: keyof ChecklistHttpService;
    params: unknown[];
  }): Promise<boolean> {
    const params = action.params;
    const checklistId = params[0];
    const itemId = params[1];
    const customInputId = params[2];
    const fileId = params[3];

    if (!(await this.removeProcessedAction(action))) {
      return false;
    }
    if (
      typeof checklistId === 'string' &&
      typeof itemId === 'string' &&
      typeof customInputId === 'string' &&
      typeof fileId === 'string'
    ) {
      this.rejectedUploadFileIds.add(fileId);
      this.fileRefs.removeImageUploadFileRef(
        checklistId,
        itemId,
        customInputId,
        fileId
      );
      await this.offlineStorage.evictBlob(fileId);
      await this.offlineStorage.markQueuedDeleteUploadRejected(fileId);
      this.appToastService.showImageUploadRejected();
    }
    return true;
  }

  private async rollbackRejectedDelete(action: {
    actionType: keyof ChecklistHttpService;
    params: unknown[];
  }): Promise<boolean> {
    const params = action.params;
    const checklistId = params[0];
    const itemId = params[1];
    const fileId = params[2];
    const rollbackContext = this.parseDeleteRollbackContext(params[3]);

    if (!(await this.removeProcessedAction(action))) {
      return false;
    }
    if (
      typeof fileId === 'string' &&
      (this.rejectedUploadFileIds.has(fileId) ||
        rollbackContext?.uploadRejected === true)
    ) {
      this.rejectedUploadFileIds.delete(fileId);
      return true;
    }
    if (
      typeof checklistId === 'string' &&
      typeof itemId === 'string' &&
      rollbackContext
    ) {
      this.fileRefs.restoreImageUploadFileRef(
        checklistId,
        itemId,
        rollbackContext.customInputId,
        rollbackContext.fileRef,
        rollbackContext.fileIndex,
        rollbackContext.fileOrder
      );
      this.appToastService.showImageDeleteRejected();
    }
    return true;
  }

  private parseDeleteRollbackContext(
    value: unknown
  ): DeleteFileRollbackContext | null {
    if (typeof value !== 'object' || value === null) {
      return null;
    }

    const candidate = value as Record<string, unknown>;
    const fileRef = this.parseDeleteRollbackFileRef(candidate['fileRef']);
    if (typeof candidate['customInputId'] !== 'string' || !fileRef) {
      return null;
    }

    const fileIndex = candidate['fileIndex'];
    const fileOrder = candidate['fileOrder'];
    return {
      customInputId: candidate['customInputId'],
      fileRef,
      fileIndex:
        typeof fileIndex === 'number' && Number.isFinite(fileIndex)
          ? fileIndex
          : undefined,
      fileOrder: Array.isArray(fileOrder)
        ? fileOrder.filter(
            (fileId): fileId is string => typeof fileId === 'string'
          )
        : undefined,
      uploadRejected: candidate['uploadRejected'] === true,
    };
  }

  private parseDeleteRollbackFileRef(
    value: unknown
  ): DeleteFileRollbackContext['fileRef'] | null {
    if (typeof value !== 'object' || value === null) {
      return null;
    }

    const file = value as Record<string, unknown>;
    if (
      typeof file['id'] !== 'string' ||
      typeof file['fileName'] !== 'string' ||
      typeof file['contentType'] !== 'string'
    ) {
      return null;
    }

    return {
      id: file['id'],
      fileName: file['fileName'],
      contentType: file['contentType'],
      originalFileName:
        typeof file['originalFileName'] === 'string'
          ? file['originalFileName']
          : undefined,
    };
  }

  private async markUploadConfirmed(
    actionType: keyof ChecklistHttpService,
    params: unknown[]
  ): Promise<void> {
    if (actionType !== 'uploadFile') {
      return;
    }

    const fileId = params[3];
    if (typeof fileId === 'string') {
      await this.offlineStorage.markUploadConfirmed(fileId);
    }
  }

  private async refreshSyncedChecklists(syncedItems: Set<string>) {
    for (const checklistId of syncedItems) {
      try {
        await this.checklistLoader.loadChecklist(checklistId);
      } catch (error) {
        console.error(`Failed to refresh checklist: ${checklistId}`, error);
      }
    }
  }
}

function fieldKeyOf(write: QueuedConditionalWrite): string {
  const [checklistId, , itemId, customInputId] = write.ids;
  return [write.actionType, checklistId, itemId, customInputId]
    .filter(part => part !== undefined)
    .join('|');
}

function mergeOntoPreviousValue(
  base: string,
  previousValue: string,
  local: string,
  multiline: boolean
): string {
  return mergeTexts(base, previousValue, local, multiline).text;
}

function textOf(payload: unknown): string {
  const value = payloadValue(payload);
  return typeof value === 'string' ? value : '';
}

function sameValue(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}
